import { useEffect, useState } from "react";
import {
  Create,
  Edit,
  ReferenceInput,
  SimpleForm,
  TextInput,
  AutocompleteInput,
  NumberInput,
  BooleanInput,
  ReferenceField,
  SelectInput,
  useNotify,
  useRefresh,
  useRecordContext,
  SelectArrayInput,
  ImageField,
  SimpleFormIterator,
  ArrayInput,
  DateInput,
  useSimpleFormIteratorItem,
} from "react-admin";
import { useFormContext, useFormState } from "react-hook-form";
import GennevApiService from "../../services/gennev-api.service";

import CustomUserNameField from "./user.util";
import { Box, IconButton, Stack } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";

const ProviderTitle = () => {
  const record = useRecordContext();
  return <span>{record ? "Edit provider" : "Create Provider"}</span>;
};

const TODAY = new Date().toISOString().split("T")[0];
const validateInactivePeriod = (
  field,
  value,
  allValues,
  props,
  hasChanged = false
) => {
  const index = getInactiveElementIndex(props);
  const currentElementValues = allValues?.tablespaces?.[index];
  const startDate = currentElementValues?.inactive_period_start;
  const endDate = currentElementValues?.inactive_period_end;
  if (field === "start") {
    if (!value && endDate) {
      return "Inactive start date is required";
    }
    if (hasChanged && value && value < TODAY) {
      return "Start date cannot be in the past";
    }
    if (value && endDate && value > endDate) {
      return "Start date cannot be after end date";
    }
  } else if (field === "end") {
    if (!value && startDate) {
      return "Inactive end date is required";
    }
    if (hasChanged && value && value < TODAY) {
      return "End date cannot be in the past";
    }
    if (value && startDate && value < startDate) {
      return "End date cannot be before start date";
    }
  }
  return undefined;
};

// Helper function to extract array index from field source
const getInactiveElementIndex = (props) => {
  const final = props?.finalSource || props?.source;
  if (!final) return -1;
  const parts = final.split(".");
  const idx = parseInt(parts[1], 10);
  return isNaN(idx) ? -1 : idx;
};

export const ProviderCreate = (props) => {
  const [states, setStates] = useState([]);
  const notify = useNotify();
  const refresh = useRefresh();

  const onCreateSuccess = () => {
    notify("Provider created successfully", { type: "success" });
    refresh();
  };

  const onCreateFailure = (error) => {
    notify(
      typeof error === "string"
        ? error
        : error.message || "ra.notification.http_error",
      { type: "warning" }
    );
    console.log(error);
  };

  // Simplified validation calls
  const validateInactivePeriodStart = (value, allValues, props) =>
    validateInactivePeriod("start", value, allValues, props);

  const validateInactivePeriodEnd = (value, allValues, props) =>
    validateInactivePeriod("end", value, allValues, props);

  useEffect(() => {
    GennevApiService.getStates().then(({ data }) => {
      setStates(data);
    });
  }, []);

  return (
    <Create {...props} mutationOptions={{ onError: onCreateFailure }}>
      <SimpleForm>
        <ReferenceInput
          label="User"
          required={true}
          source="user_id"
          reference="admin/user"
          perPage={50}
        >
          <AutocompleteInput
            optionText="email"
            label="User"
            filterToQuery={(searchText) => ({ email: searchText })}
          />
        </ReferenceInput>
        <SelectInput
          source="provider_type"
          required={true}
          choices={[
            { id: "coach", name: "Coach" },
            { id: "doctor", name: "Doctor" },
          ]}
        />
        <SelectArrayInput
          source="care_types"
          required={true}
          optionText="name"
          optionValue="id"
          choices={[
            { id: "coach", name: "Coach" },
            { id: "doctor", name: "Doctor" },
            { id: "wm", name: "Weight Management" },
          ]}
        />
        <NumberInput source="acuity_calendar_id" />
        <BooleanInput source="is_active" />
        <BooleanInput source="self_pay" />
        <TextInput source="title" required={true} />
        <TextInput source="designation" />
        <TextInput source="board_certification" />
        <SelectInput
          source="timezone"
          required={true}
          choices={[
            { id: "Pacific/Honolulu", name: "Hawaii" },
            { id: "America/Anchorage", name: "Alaska" },
            { id: "America/Los_Angeles", name: "West Cost / Pacific" },
            { id: "America/Phoenix", name: "Arizona" },
            { id: "America/Denver", name: "Mountain" },
            { id: "America/Chicago", name: "Central" },
            { id: "America/New_York", name: "East Cost" },
          ]}
        />
        <TextInput source="specialities" />
        <TextInput source="credentials" />
        <TextInput source="profile_image_url" />
        <TextInput source="clinical_experience" multiline rows={4} />
        <TextInput source="medical_experience" multiline rows={4} />
        <TextInput source="past_experience" multiline rows={4} />
        <TextInput source="education" multiline rows={4} />
        <TextInput source="alumni_of" multiline rows={4} />
        <SelectArrayInput
          source="state_ids"
          choices={states}
          optionText="name"
          optionValue="id"
        />
        <ArrayInput source="tablespaces" name="tablespaces">
          <SimpleFormIterator inline>
            <ReferenceInput
              source="athena_tablespace_id"
              reference="admin/medical/tablespaces"
            />
            <NumberInput source="athena_provider_id" helperText={false} />
            <DateInput
              source="inactive_period_start"
              label="Inactive Start"
              validate={validateInactivePeriodStart}
            />
            <ClearDateButton field="inactive_period_start" />
            <DateInput
              source="inactive_period_end"
              label="Inactive End"
              validate={validateInactivePeriodEnd}
            />
            <ClearDateButton field="inactive_period_end" />
          </SimpleFormIterator>
        </ArrayInput>
      </SimpleForm>
    </Create>
  );
};

export const ProviderEdit = (props) => {
  const { mutationMode } = props;
  const notify = useNotify();
  const refresh = useRefresh();
  const [states, setStates] = useState([]);
  useEffect(() => {
    refresh();
    GennevApiService.getStates().then(({ data }) => {
      setStates(data);
    });
  }, []);

  const onUpdateFailure = (error) => {
    notify(
      typeof error === "string"
        ? error
        : error.message || "ra.notification.http_error",
      { type: "warning" }
    );
    console.log(error);
    if (mutationMode === "undoable" || mutationMode === "pessimistic") {
      refresh();
    }
  };

  const EditFormBody = () => {
    const initialValuesRef = useFormState({
      defaultValues: true,
    }).defaultValues;

    const validateInactivePeriodStart = (value, allValues, props) => {
      const index = getInactiveElementIndex(props);
      const initialTablespace = initialValuesRef?.tablespaces?.[index];
      const initialValue =
        initialTablespace?.inactive_period_start?.split("T")[0];
      const hasChanged = value !== initialValue;
      return validateInactivePeriod(
        "start",
        value,
        allValues,
        props,
        hasChanged
      );
    };
    const validateInactivePeriodEnd = (value, allValues, props) => {
      const index = getInactiveElementIndex(props);
      const initialTablespace = initialValuesRef?.tablespaces?.[index];
      const initialValue =
        initialTablespace?.inactive_period_end?.split("T")[0];
      const hasChanged = value !== initialValue;
      return validateInactivePeriod("end", value, allValues, props, hasChanged);
    };

    return (
      <>
        <ReferenceField
          label="Full Name"
          source="user_id"
          reference="admin/user"
          sx={{ mb: 2 }}
        >
          <CustomUserNameField label="Full Name" />
        </ReferenceField>
        <SelectArrayInput
          source="care_types"
          required={true}
          optionText="name"
          optionValue="id"
          choices={[
            { id: "coach", name: "Coach" },
            { id: "doctor", name: "Doctor" },
            { id: "wm", name: "Weight Management" },
          ]}
        />
        <NumberInput source="acuity_calendar_id" />
        <BooleanInput source="is_active" />
        <BooleanInput source="self_pay" />
        <TextInput source="title" required={true} />
        <TextInput source="designation" />
        <TextInput source="board_certification" />
        <SelectInput
          source="timezone"
          required={true}
          choices={[
            { id: "Pacific/Honolulu", name: "Hawaii" },
            { id: "America/Anchorage", name: "Alaska" },
            { id: "America/Los_Angeles", name: "West Cost / Pacific" },
            { id: "America/Phoenix", name: "Arizona" },
            { id: "America/Denver", name: "Mountain" },
            { id: "America/Chicago", name: "Central" },
            { id: "America/New_York", name: "East Cost" },
          ]}
        />
        <TextInput source="specialities" />
        <TextInput source="credentials" />
        <TextInput source="clinical_experience" multiline rows={4} />
        <TextInput source="medical_experience" multiline rows={4} />
        <TextInput source="past_experience" multiline rows={4} />
        <TextInput source="education" multiline rows={4} />
        <TextInput source="alumni_of" multiline rows={4} />
        <Box>
          <Stack
            direction="row"
            justifyContent="stretch"
            alignItems="center"
            spacing={2}
          >
            <TextInput source="profile_image_url" sx={{ flexGrow: 1 }} />
            <ImageField source="profile_image_url" title="profile_image_url" />
          </Stack>
        </Box>
        <SelectArrayInput
          source="state_ids"
          choices={states}
          optionText="name"
          optionValue="id"
        />
        <ArrayInput source="tablespaces" name="tablespaces">
          <SimpleFormIterator inline>
            <ReferenceInput
              source="athena_tablespace_id"
              reference="admin/medical/tablespaces"
            />
            <NumberInput source="athena_provider_id" helperText={false} />
            <DateInput
              source="inactive_period_start"
              label="Inactive Start"
              validate={validateInactivePeriodStart}
            />
            <ClearDateButton field="inactive_period_start" />
            <DateInput
              source="inactive_period_end"
              label="Inactive End"
              validate={validateInactivePeriodEnd}
            />
            <ClearDateButton field="inactive_period_end" />
          </SimpleFormIterator>
        </ArrayInput>
      </>
    );
  };

  return (
    <Edit
      title={<ProviderTitle />}
      {...props}
      mutationOptions={{ onError: onUpdateFailure }}
    >
      <SimpleForm>
        <EditFormBody />
      </SimpleForm>
    </Edit>
  );
};

const ClearDateButton = ({ field }) => {
  const { setValue } = useFormContext();
  const { index } = useSimpleFormIteratorItem();
  const onClear = () => {
    setValue(`tablespaces.${index}.${field}`, null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  return (
    <IconButton size="small" onClick={onClear}>
      <ClearIcon />
    </IconButton>
  );
};
